import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FlaskConical,
  LayoutDashboard,
  LogOut,
  ClipboardCheck,
  Search,
  Mail,
  User,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { BUSINESS } from "../constants/business";

const AdminLink = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase()}`}
    className={`flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-wider font-semibold rounded transition-colors ${
      active
        ? "bg-white text-brand"
        : "text-white/85 hover:bg-white/15"
    }`}
  >
    <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
    <span className="hidden sm:inline">{label}</span>
  </Link>
);

const Navbar = () => {
  const { pathname } = useLocation();

  // Admin authentication
  const { user, logout: adminLogout } = useAuth();

  // Customer authentication
  const {
    customer,
    logout: customerLogout,
  } = useCustomerAuth();

  return (
    <header className="sticky top-0 z-50 bg-brand text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4 flex-wrap">

        {/* Product brand */}
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 min-w-0"
        >
          <div className="w-10 h-10 bg-white flex items-center justify-center rounded-md shadow-sm shrink-0">
            <span className="text-brand font-heading font-black text-lg leading-none">
              PV
            </span>
          </div>

          <div className="leading-tight min-w-0">
            <div className="font-heading font-black text-base sm:text-lg tracking-tight truncate">
              {BUSINESS.product}
            </div>

            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-white/85 truncate">
              {BUSINESS.tagline}
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-4 sm:gap-5 text-[11px] sm:text-xs">

          {/* Support email */}
          <a
            href={`mailto:${BUSINESS.support_email}`}
            className="flex items-center gap-1.5 hover:underline"
          >
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">
              {BUSINESS.support_email}
            </span>
          </a>

          {/* Admin-only tools */}
          {user && (
            <div className="flex items-center gap-1 border-l border-white/25 pl-3 ml-1">

              <AdminLink
                to="/"
                icon={Search}
                label="Search"
                active={pathname === "/"}
              />

              <AdminLink
                to="/test"
                icon={FlaskConical}
                label="Test"
                active={pathname === "/test"}
              />

              <AdminLink
                to="/evaluate"
                icon={ClipboardCheck}
                label="Evaluate"
                active={pathname === "/evaluate"}
              />

              <AdminLink
                to="/admin"
                icon={LayoutDashboard}
                label="Admin"
                active={pathname.startsWith("/admin")}
              />

              <button
                data-testid="admin-logout-btn"
                onClick={adminLogout}
                className="flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-wider font-semibold rounded text-white/85 hover:bg-white/15"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          )}

          {/* Customer account */}
          {customer && !user && (
            <div className="flex items-center gap-2 border-l border-white/25 pl-3 ml-1">

              <div className="flex items-center gap-1.5 text-white/90">
                <User className="w-4 h-4" />

                <span className="hidden sm:inline">
                  {customer.mobile || "Customer"}
                </span>
              </div>

              <button
                data-testid="customer-logout-btn"
                onClick={customerLogout}
                className="flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-wider font-semibold rounded text-white/85 hover:bg-white/15"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Logout</span>
              </button>

            </div>
          )}

        </div>
      </div>
    </header>
  );
};

export default Navbar;
